import { inventoryFloorRefusal } from '../compile/inventory-facet';
// Pure approach along the floor plane: travel at most N ft, stopping 5.1 ft from the
// target centre, with the shared structural stop and forced-move destination resolver.
// This reposition has no per-foot move/reach triggers, traps, falls or zone entry.
// Compounds, unreadable geometry and nearby solid inventory retain named refusals.
import { destination, groundClamp, inventoryBlocksSweep, type ForcedMoveReaders, type Point } from './forced-movement.js';
import { type RestShape } from '../inventory/floor-move.js';
import { compoundActorIds } from './compound-drag.js';
import { wallStop } from './wall-stop.js';
import { withExteriorOpenGround } from '../inventory/lift-occupancy.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter(v => record(v) !== null) as Row[] : [];
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const EPS = 1e-6;
/** move_caster_moves_towards_target_in_ft(): the stop short of the target's centre. */
export const WALK_TOWARD_SAFE_FT = 5.1;

export interface WalkTowardInput {
  encounter: Row;
  shape: RestShape | null;
  mover: Row;
  /** The target row (its pos_coord x / y). */
  target: Row;
  /** discern_variable_tags(caster_moves_towards_target_in_ft) — a plain number here. */
  moveFt: number;
}
export type WalkTowardResult =
  | { kind: 'none'; why: 'no-distance' | 'at-target' | 'within-reach' | 'destination-blocked' | 'no-displacement' }
  | { kind: 'move'; start: Point; end: Point; nudged: boolean }
  | { kind: 'handback'; reason: string };
const hand = (reason: string): WalkTowardResult => ({ kind: 'handback', reason: 'walk-toward:' + reason });

/** pos_coord(): only the position container (`position`, else `pos_arr`); an elided axis is null. */
function coord(row: Row, axis: 'x_pos' | 'y_pos' | 'z_pos'): number | null {
  const position = record(row.position) ?? record(row.pos_arr);
  if (!position || !(axis in position)) return null;
  const raw = scalar(position[axis]);
  return typeof raw === 'number' || (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))) ? Number(raw) : null;
}
const COMPOUND_LINKS = ['riding', 'ridden', 'piloting', 'piloted', 'grappling', 'grappled'];

export function resolveWalkToward(input: WalkTowardInput, read: ForcedMoveReaders): WalkTowardResult {
  const { encounter, mover, target, moveFt } = input;
  let shape = read.runtimeShape ?? input.shape;
  const floorRefusal = inventoryFloorRefusal(encounter);
  if (floorRefusal) return hand(floorRefusal);
  if (!Number.isFinite(moveFt)) return hand('distance-formula');
  if (!(moveFt > 0)) return { kind: 'none', why: 'no-distance' };
  if (shape && !Array.isArray(shape.boxes)) return hand('legacy-axes');
  // the UNDIRECTED compound (ft_compound_actor_ids): a rider whose Riding row points at the mover moves with it even when the mover
  // carries no reciprocal status (Codex review 2026-09-30) — any member but the mover itself hands back
  if (COMPOUND_LINKS.some(key => read.hasStatus(mover, key)) || compoundActorIds(encounter, mover, read).size > 1) return hand('compound');
  // the FT3D branch: pos_coord x / y of both rows; a target with neither axis → PHP returns (nothing moves)
  const tx = coord(target, 'x_pos'), ty = coord(target, 'y_pos');
  if (tx === null && ty === null) return { kind: 'none', why: 'at-target' };
  const start = { x: coord(mover, 'x_pos') ?? 0, y: coord(mover, 'y_pos') ?? 0, z: coord(mover, 'z_pos') ?? 0 };
  const dx = (tx ?? 0) - start.x, dy = (ty ?? 0) - start.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 0.01) return { kind: 'none', why: 'at-target' };
  const travel = Math.min(dist - WALK_TOWARD_SAFE_FT, moveFt);
  if (travel <= 0) return { kind: 'none', why: 'within-reach' };
  let end: Point = { x: start.x + dx / dist * travel, y: start.y + dy / dist * travel, z: start.z };
  const base = read.baseSizeFt(mover), height = read.heightFt(mover);
  if (base === null || !(base > 0) || height === null) return hand('body');
  if (!shape || !shape.boxes.length) {
    // An unshaped canvas has open ground at z=0, as the plain walk does. Retain
    // scene walls/overlays when present and use the same occupancy/placement resolver.
    if (Math.abs(start.z) > EPS) return hand('legacy-axes');
    const margin = 40 + base;
    shape = { ...shape, boxes: [{ id: 'navigation-open-ground', navigationOpenGround: true,
      x: { min: Math.min(start.x, end.x) - margin, max: Math.max(start.x, end.x) + margin },
      y: { min: Math.min(start.y, end.y) - margin, max: Math.max(start.y, end.y) + margin }, floor: 0, ceiling: 1000 }], openings: shape?.openings ?? [] };
  }
  const stopped = wallStop(shape, start, end, base, height);
  end = stopped.end;
  shape = withExteriorOpenGround(shape, start.x, start.y, end.x, end.y, 40 + base,
    Number(scalar(record(encounter.encounters)?.encounter_inside) ?? 0) !== 1);
  // Solid inventory within the path or landing envelope retains its existing named refusal.
  const inventoryNear = (from: Point, to: Point, radius: number): string | null => {
    for (const inv of rows(encounter.encounter_inventories_arr)) {
      const blocks = inventoryBlocksSweep(inv, read, mover);
      if (blocks === false) continue;
      const extent = read.inventoryExtentFt(inv);
      if (blocks === null || extent === null) return 'inventory-definition';
      const [ix, iy] = read.position(inv);
      if (![ix, iy].every(Number.isFinite)) return 'inventory-definition';
      const bound = extent + radius + 1;
      const len = Math.hypot(to.x - from.x, to.y - from.y);
      const t = len < EPS ? 0 : Math.max(0, Math.min(1, ((ix - from.x) * (to.x - from.x) + (iy - from.y) * (to.y - from.y)) / (len * len)));
      if (Math.hypot(ix - (from.x + t * (to.x - from.x)), iy - (from.y + t * (to.y - from.y))) <= bound)
        return read.inventoryCollision?.(inv)?.obstacle.kind === 'oblong' ? 'nonuniform-prop-extent' : 'inventory';
    }
    return null;
  };
  const near = inventoryNear(start, end, base / 2);
  if (near) return hand(near);
  // move_target()'s FT3D path: ft_resolve_move_destination(…, walk snap), a refused destination leaves the row where it was
  const landed = destination({ encounter, shape }, { ...read, runtimeShape: read.runtimeShape ? shape : undefined }, mover, start, end, false, stopped.wallContact);
  if (landed.status === 'unknown') return hand('destination-resolver:' + landed.reason);
  if (landed.status === 'blocked') return { kind: 'none', why: 'destination-blocked' };
  end = groundClamp(shape, mover, start.z, { x: landed.x, y: landed.y, z: landed.z }, read);
  const nudgedNear = landed.moved ? inventoryNear(end, end, base / 2) : null;
  if (nudgedNear) return hand(nudgedNear);
  // unchanged only when every axis stores the same DECIMAL(…, 3) value (a sub-0.001 ft travel can still round to a new coordinate)
  if ((['x', 'y', 'z'] as const).every(a => end[a].toFixed(3) === start[a].toFixed(3))) return { kind: 'none', why: 'no-displacement' };
  return { kind: 'move', start, end, nudged: landed.moved };
}
